import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link,
  useToast,
  Icon,
  Flex,
  useColorModeValue,
  VStack,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import { RESET_PASSWORD_URL } from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [form, setForm] = useState({
    tenantSlug: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const bgMain = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textPrimary = useColorModeValue("gray.700", "gray.100");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      return toast({
        title: "Invalid Link",
        description:
          "Reset token is missing. Please use the link from your email.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    if (!form.tenantSlug || !form.newPassword || !form.confirmPassword) {
      return toast({
        title: "Missing Fields",
        description: "All fields are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (form.newPassword !== form.confirmPassword) {
      return toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords match.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (form.newPassword.length < 6) {
      return toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);

    try {
      const response = await axios.post(RESET_PASSWORD_URL, {
        tenantSlug: form.tenantSlug,
        token: token,
        newPassword: form.newPassword,
      });

      toast({
        title: "Password Reset Successful",
        description:
          response.data.message || "You can now login with your new password.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to reset password.";
      toast({
        title: "Reset Failed",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg={bgMain} p={4}>
      <Box
        width="100%"
        maxW="480px"
        boxShadow="lg"
        rounded="xl"
        overflow="hidden"
        bg={cardBg}
        p={8}
      >
        <VStack spacing={6}>
          <Icon as={FaLock} boxSize={10} color="teal.400" />
          <Heading size="lg" color={textPrimary} textAlign="center">
            Reset Password
          </Heading>
          <Text fontSize="sm" color={textSecondary} textAlign="center">
            Enter your tenant slug and choose a new secure password.
          </Text>

          <Stack spacing={4} width="100%">
            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textPrimary}>
                Tenant Slug
              </FormLabel>
              <Input
                name="tenantSlug"
                value={form.tenantSlug}
                onChange={handleChange}
                placeholder="e.g. vishal"
                autoComplete="organization"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textPrimary}>
                New Password
              </FormLabel>
              <InputGroup>
                <Input
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={form.newPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <InputRightElement>
                  <Icon
                    as={showPassword ? FaEyeSlash : FaEye}
                    cursor="pointer"
                    onClick={() => setShowPassword(!showPassword)}
                    color={textSecondary}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Minimum 6 characters
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textPrimary}>
                Confirm New Password
              </FormLabel>
              <InputGroup>
                <Input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <InputRightElement>
                  <Icon
                    as={showConfirmPassword ? FaEyeSlash : FaEye}
                    cursor="pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    color={textSecondary}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Button
              colorScheme="teal"
              size="lg"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Resetting..."
            >
              Reset Password
            </Button>

            <Text fontSize="sm" textAlign="center" color={textPrimary}>
              Remember your password?{" "}
              <Link
                color="teal.400"
                fontWeight="medium"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </Link>
            </Text>
          </Stack>
        </VStack>
      </Box>
    </Flex>
  );
};

export default ResetPasswordPage;
